/**
 * Step identity for linking-flow telemetry.
 *
 * The iframe's real URL never changes (enum-driven navigation via PageSwitcher,
 * no router), so every event lands on one page and no funnel can be built from
 * them. We keep the current step and its dimensions here, in the main bundle,
 * and the telemetry plugin publishes a virtual URL per step from it.
 *
 * This module holds no PostHog dependency on purpose: the plugin that owns
 * PostHog is a separate build artifact, injected at runtime, and may never load
 * (kill switch, ad blocker, network policy). Reading this state through the
 * bridge on `window.mergeLinkTelemetry` keeps the main bundle telemetry-free.
 */

import { LinkingFlowTelemetryContext } from "../telemetry/bridge";

export type { LinkingFlowTelemetryContext };

/**
 * Dimensions that belong to one step rather than to the whole session. They are
 * dropped on every step change, so navigating back from CONNECTOR_SETUP to
 * AUTH_CHOICE stops reporting the auth type the user has just abandoned.
 *
 * `connector` is deliberately absent: it is chosen once and still applies on
 * steps whose payload omits it, such as SUCCESS. The customer organization is
 * not here at all — it comes from the link token, once per session, and never
 * travels with a step.
 */
const STEP_SCOPED_KEYS: Array<keyof LinkingFlowTelemetryContext> = ["authType"];

let context: LinkingFlowTelemetryContext = { step: null };

/**
 * Records the step the user is now on, plus the dimensions the funnel is sliced
 * by. Returns true when the step actually changed, so the caller can emit one
 * pageview per transition rather than one per render.
 */
export const setLinkingFlowContext = (next: LinkingFlowTelemetryContext): boolean => {
  const stepChanged = next.step !== context.step;

  // A step change starts the step-scoped dimensions over, so an absent value on
  // the new step clears the old one rather than carrying it forward.
  const carried = { ...context };
  if (stepChanged) {
    STEP_SCOPED_KEYS.forEach((key) => delete carried[key]);
  }

  // Dimensions can arrive a render after the step does, as the payload fills in,
  // so they are merged rather than replaced. Undefined is dropped: the call site
  // passes every key on every render, and spreading an absent one would wipe a
  // session-scoped dimension we already know (SUCCESS carries no connector).
  const defined = Object.fromEntries(
    Object.entries(next).filter(([, value]) => value !== undefined)
  ) as Partial<LinkingFlowTelemetryContext>;

  context = { ...carried, ...defined, step: next.step };

  return stepChanged;
};

export const getLinkingFlowContext = (): LinkingFlowTelemetryContext => context;

/**
 * Clears the step between Link sessions.
 *
 * `closeLink()` only hides the iframe, so this module's state outlives a close.
 * Without this, an end user who closes on a step and reopens onto the same step
 * produces no pageview, and the top of the funnel loses every reopen.
 */
export const resetStepTracking = (): void => {
  context = { step: null };
};
